import React, { useEffect, useMemo, useState } from 'react'
import { useAuthStore } from '../../store/authStore'

const MOVE_MS = 12_000

/** Soft grid positions — keeps label readable without covering center action area. */
const POSITIONS: Array<{ top: string; left: string }> = [
  { top: '18%', left: '8%' },
  { top: '22%', left: '62%' },
  { top: '48%', left: '12%' },
  { top: '52%', left: '58%' },
  { top: '68%', left: '28%' },
  { top: '72%', left: '55%' },
]

function buildWatermarkLabel(user: {
  id?: number
  email?: string
  fullName?: string
} | null): string {
  if (!user) return ''
  const email = (user.email || '').trim()
  const name = (user.fullName || '').trim()
  const idPart = user.id != null ? `ID:${user.id}` : ''
  const primary = email || name || idPart
  if (!primary) return ''
  if (email && idPart) return `${email} · ${idPart}`
  return primary
}

/**
 * Floating identity watermark for student video surfaces.
 * Survives container fullscreen; does not block controls (pointer-events: none).
 * Note: not burned into downloaded MP4 files — mainly deters screen-share / screen-record leaks.
 */
const VideoWatermark: React.FC = () => {
  const user = useAuthStore((state) => state.user)
  const label = useMemo(() => buildWatermarkLabel(user), [user])
  const [index, setIndex] = useState(() => Math.floor(Math.random() * POSITIONS.length))

  useEffect(() => {
    if (!label) return
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1 + Math.floor(Math.random() * (POSITIONS.length - 1))) % POSITIONS.length)
    }, MOVE_MS)
    return () => window.clearInterval(timer)
  }, [label])

  if (!label) return null

  const pos = POSITIONS[index] || POSITIONS[0]

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 z-20 overflow-hidden select-none"
    >
      {/* Soft diagonal repeat — harder to crop a clean frame */}
      <div
        className="absolute inset-[-20%] flex flex-wrap content-around justify-around opacity-[0.07]"
        style={{ transform: 'rotate(-22deg)' }}
      >
        {Array.from({ length: 18 }).map((_, i) => (
          <span
            key={i}
            className="mx-6 my-8 whitespace-nowrap text-[11px] font-medium tracking-wide text-white sm:text-xs"
          >
            {label}
          </span>
        ))}
      </div>

      {/* Primary moving label */}
      <div
        className="absolute max-w-[70%] transition-[top,left] duration-700 ease-out"
        style={{ top: pos.top, left: pos.left }}
      >
        <span
          className="block truncate rounded px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-white/45 sm:text-xs"
          style={{ textShadow: '0 1px 2px rgba(0,0,0,0.55)' }}
        >
          {label}
        </span>
      </div>
    </div>
  )
}

export default VideoWatermark
